import { ValidationErrors } from 'Interfaces/ValidationErrors';

export type LoginResponseError = [] | {};

export interface LoginResponse {
    success: boolean;
    data: UserLoginDataResponse;
    error: LoginResponseError;
}

export interface UserLoginDataResponse {
    isLoggedIn: boolean;
    isUserRegistered?: false | '1'; // don't ask
    errors?: ValidationErrors;
    isUserAlreadyExisting?: boolean;
    shouldShowDataPolicyOverlay?: boolean; // found in response
    sessionType?: SessionTypes;
}

export enum Currency {
    EUR = 'EUR',
    CHF = 'CHF',
    PLN = 'PLN',
    CZK = 'CZK',
}

export interface SpeedCurveConfig {
    enabled: boolean;
    LUXId: string;
}

export interface Config {
    aliceCookieRefreshUrl: string;
    speedCurve: SpeedCurveConfig;
    buildVersion: string;
    shopApiBaseUrl: string;
    staticHost: string;
    staticCmsHost: string;
    bellaApiBaseUrl: string;
    gtmAccountId: string;
    facebookAppId: string;
    listingPageSize: number;
    currency: Currency;
    countryCode: string;
    languages: { [key: string]: string };
    hrefLangsDomains: { [key: string]: string };
    env: string;
    preconnectUrls: string[];
    shopToClubOverlayId: string;
    clubSignupOverlayId: string;
    hotjarId: string;
    segmentWriteKey: string;
    segmentBaseCDNURL: string;
    lisaBaseUrl: string;
    bobBaseUrl: string;
    links: {
        clubBaseUrl: string;
        url: string;
        facebook: string;
        facebook2: string; // todo: (P100-2025)  once OneFooter is rolled out we can use facebook for it and remove facebook2. Also change in config.
        instagram: string;
        pinterest: string;
        youtube: string;
        apple: string;
        android: string;
        contact: string;
        press: string;
        imprint: string;
        faq: string;
        brands: string;
        agb: string;
        interiorService: string;
        career: string;
        dataProtection: string;
        creditCheckPolicy: string;
        forgotPassword: string;
        home: string;
        cart: string;
        wishList: string;
        looks: string;
        login: string;
        logout: string;
        myAccount: string;
        myWishlist: string;
        myOrders: string;
        myReviews: string;
        myCoupons: string;
        myEditAccount: string;
        myAddress: string;
        myNewsletter: string;
        myNotifications: string;
        respondentContact: string;
        clubReferAFriendPath: string;
        westwingCollection: string;
        helpCenterUrl: string;
        helpCenterContactUrl: string;
        moodboard: string;
        moodboardLandingPage: string;
        singleSignOn: string;
        olapicUgcGallery: string;
        styleHub: string;
        appsPromoPage: string;
    };
    companyData: {
        contactPoint: {
            contactType: string;
        };
        hoursAvailable: {
            dayOfWeek: string[];
        }[];
    };
    availableCountryCodes: {
        de: string;
        at: string;
        it: string;
        ch: string;
        nl: string;
        pl: string;
        es: string;
        fr: string;
        cz: string;
        sk: string;
    };
}
export enum ClubRegistrationStatus {
    NEW_REGISTRATION = 'NEW_REGISTRATION',
    EXISTING_USER_SUBSCRIBED = 'EXISTING_USER_SUBSCRIBED',
    EXISTING_USER_NOT_SUBSCRIBED = 'EXISTING_USER_NOT_SUBSCRIBED',
    WRONG_PASSWORD = 'WRONG_PASSWORD',
}

export enum OverlayId {
    LOGIN_AND_REGISTER = 'LOGIN_AND_REGISTER',
    SHOP_TO_CLUB_REGISTRATION_SINGLE_STEP_OVERLAY = 'SHOP_TO_CLUB_REGISTRATION_SINGLE_STEP_OVERLAY',
    SHOP_TO_CLUB_REGISTRATION_MULTI_STEP_OVERLAY = 'SHOP_TO_CLUB_REGISTRATION_MULTI_STEP_OVERLAY',
    DOUBLE_OPT_IN_OVERLAY = 'DOUBLE_OPT_IN_OVERLAY',
    CLUB_SIGNUP_OVERLAY = 'CLUB_SIGNUP_OVERLAY',
    BLOCKED_USER = 'BLOCKED_USER',
}

export type ShopToClubOverlay =
    | typeof OverlayId.SHOP_TO_CLUB_REGISTRATION_SINGLE_STEP_OVERLAY
    | typeof OverlayId.SHOP_TO_CLUB_REGISTRATION_MULTI_STEP_OVERLAY;

export enum UpdateLoginSateActions {
    LOGIN = 'LOGIN',
    LOGOUT = 'LOGOUT',
    EXISTING_USER_LOGIN_FAIL = 'EXISTING_USER_LOGIN_FAIL',
}

export interface ShopToClubOverlayConfig {
    mobileImage: string;
    mobileDelayInSeconds: number;
    expirationInDays: number;
    isDismissible: boolean;
    isUndismissibleFromOutsideClick: boolean;
    pageViewLimit: string;
    getS2cBannerFontFromCountry: (country: string) => FontSizeConfig;
}

export enum SessionTypes {
    unknown = 'unknown',
    partial = 'partial',
    full = 'full',
}

export interface FontSizeConfig {
    mobileFontSize: number;
    desktopFontSize: number;
}

export interface OverlayOpenOptions {
    isLogin?: boolean;
    redirectUrl?: string;
    email?: string;
    trackingLabel?: string;
    isDismissible?: boolean;
    isUndismissibleFromOutsideClick?: boolean;
    partialLoginEmail?: string;
}

export interface Option {
    key: keyof OverlayOpenOptions;
    set: Function;
}

export enum PageType {
    LANDING = 'n',
    CATEGORY = 'category',
    BRANDS = 'brands',
    NEWPRODUCTS = 'new-products',
    ALLPRODUCTS = 'all-products',
    STYLES = 'styles', // obsolete PageType: every call to this page should lead to ALLPRODUCTS instead
    HOMEPAGE = 'homepage',
    PDP = 'PDP',
    LLP = 'LLP',
    LOOKS = 'looks',
    ALICE_OTHER = 'alice-other',
    ALICE_WISHLIST = 'alice-wishlist',
    ALICE_INTERIOR_DESIGN_SERVICE = 'alice-interior-design-service',
    ALICE_STATIC_PAGE = 'alice-static-page',
    EXTERNAL = 'external',
    CART = 'cart',
    CART_NATIVE = 'cart-native',
    ALICE_CART = 'alice-cart',
    UNKNOWN = 'unknown',
    WISHLIST = 'wishlist',
    WISHLIST_NATIVE = 'wishlist-native',
    INTERIOR_DESIGN_SERVICE = 'interior-design-service',
    INTERIOR_DESIGN_SERVICE_NATIVE = 'interior-design-service-native',
    CHECKOUT = 'checkout',
    CHECKOUT_NATIVE = 'checkout-native',
    ASSEMBLYSERVICE = 'assemblyservice-app-webview',
}

export interface ICredentials {
    email: string;
    password: string;
}
